import type { ResultOf } from '@graphql-typed-document-node/core'
import { atom, useAtomValue } from 'jotai'
import type { Atom } from 'jotai'
import { atomEffect } from 'jotai-effect'
import { atomWithQuery } from 'jotai-tanstack-query'
import { useCallback } from 'react'
import type { FragmentType } from '~/lib/graphql'
import { getFragmentData } from '~/lib/graphql'
import type { DiscountCoupon } from '~/lib/graphql/graphql'
import type { DateText, DateTime, DiscountCouponCode, PlanId } from '~/lib/graphql/scalars'
import { money } from '~/lib/money/money'
import { createQueryAtom, createQueryOptions } from '~/stores/jotaiQueryHelper'
import { now$ } from '~/stores/now'
import { totalAmount$ } from './payment'
import { scope } from './scope'
import { discountCouponType$, restaurantId$ } from './stateMachine/context'
import { MemberDiscountCoupons } from './stateMachine/memberDiscountCounpon.frag'
import type {
  ReserveInputMember,
  ReserveInputPlan,
  ReserveInputReserveInfo,
  ReserveInputRestaurant,
} from './stateMachine/state'
import {
  isGuest$,
  isInitialDisplay$,
  member$,
  plan$,
  reserveInfo$,
  restaurant$,
  transition$,
  useTransition,
} from './stateMachine/state'
import type { ReservationInputEvent, SelectedDiscountCouponType } from './stateMachine/statemachine'
import { ValidateDiscountCoupon } from './validateDiscountCoupon.graphql'

export const discountCoupons$: Atom<ReserveInputDiscountCoupons> = atom((get) => {
  const member = get(member$)
  const now = get(now$)
  const reserveInfo = get(reserveInfo$)
  const totalAmount = get(totalAmount$)
  // macaron-backendから取得したクーポンには期間外や最低金額以下のものが含まれているので、ここでフィルタリングし成形する
  const coupons = availableDiscountCoupons(member, reserveInfo, now, totalAmount)
  return addPriceToDuplicateName(coupons)
})
discountCoupons$.debugLabel = 'discountCoupons$'

export function useDiscountCoupons() {
  return useAtomValue(discountCoupons$)
}

function availableDiscountCoupons(
  member: ReserveInputMember | undefined,
  reserveInfo: ReserveInputReserveInfo | undefined,
  now: DateTime,
  totalAmount: number
) {
  if (!member || !reserveInfo) {
    return []
  }
  const coupons = getDiscountCoupons(member)
  return coupons.filter(
    (coupon) =>
      reservablePeriod(coupon, now) &&
      visitablePeriod(coupon, reserveInfo.visitDate) &&
      usableTotalAmount(coupon, totalAmount)
  )
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('filterDiscountCoupons', () => {
    expect(
      availableDiscountCoupons(
        {
          discountCoupons: [
            {
              name: '割引クーポン',
              price: 1000,
              code: '1234567890',
              reservablePeriod: {
                from: '2023-01-01T09:00:00.000+09:00',
                to: '2023-01-02T09:00:00.000+09:00',
              },
              visitablePeriod: { from: '2023-01-01', to: '2023-01-02' },
              minimumReservationAmount: 2000,
            },
            {
              name: '最低適用金額以下のクーポン',
              price: 2000,
              code: '1234567890',
              reservablePeriod: {
                from: '2023-01-01T09:00:00.000+09:00',
                to: '2023-01-02T09:00:00.000+09:00',
              },
              visitablePeriod: { from: '2023-01-01', to: '2023-01-02' },
              minimumReservationAmount: 20000,
            },
            {
              name: '予約可能期間外のクーポン',
              price: 2000,
              code: '1234567890',
              reservablePeriod: {
                from: '2023-01-02T09:00:00.000+09:00',
                to: '2023-01-03T09:00:00.000+09:00',
              },
              visitablePeriod: { from: '2023-01-01', to: '2023-01-02' },
              minimumReservationAmount: 2000,
            },
            {
              name: '来店可能期間外のクーポン',
              price: 2000,
              code: '1234567890',
              reservablePeriod: {
                from: '2023-01-01T09:01:00.000+09:00',
                to: '2023-01-02T09:00:00.000+09:00',
              },
              visitablePeriod: { from: '2023-01-02', to: '2023-01-03' },
              minimumReservationAmount: 2000,
            },
          ],
        } as unknown as ReserveInputMember,
        {
          visitDate: '2023-01-01' as DateText,
        } as ReserveInputReserveInfo,
        '2023-01-01T09:00:00.000+09:00' as DateTime,
        10000
      )
    ).toEqual([
      {
        name: '割引クーポン',
        price: 1000,
        code: '1234567890',
        reservablePeriod: {
          from: '2023-01-01T09:00:00.000+09:00',
          to: '2023-01-02T09:00:00.000+09:00',
        },
        visitablePeriod: { from: '2023-01-01', to: '2023-01-02' },
        minimumReservationAmount: 2000,
      },
    ])
  })
}

export type ReserveInputDiscountCoupons = ReturnType<typeof getDiscountCoupons>
export function getDiscountCoupons(member: FragmentType<typeof MemberDiscountCoupons>) {
  return getFragmentData(MemberDiscountCoupons, member).discountCoupons
}

function reservablePeriod(coupon: DiscountCoupon, now: DateTime) {
  return coupon.reservablePeriod.from <= now && now <= coupon.reservablePeriod.to
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('reservablePeriod', async () => {
    expect(
      reservablePeriod(
        {
          reservablePeriod: {
            from: '2023-01-01T09:00:00.000+09:00',
            to: '2023-01-02T09:00:00.000+09:00',
          },
        } as DiscountCoupon,
        '2023-01-01T09:00:00.000+09:00' as DateTime
      )
    ).toBe(true)
    expect(
      reservablePeriod(
        {
          reservablePeriod: {
            from: '2023-01-01T09:00:00.000+09:00',
            to: '2023-01-02T09:00:00.000+09:00',
          },
        } as DiscountCoupon,
        '2023-01-02T09:00:00.000+09:00' as DateTime
      )
    ).toBe(true)
    expect(
      reservablePeriod(
        {
          reservablePeriod: {
            from: '2023-01-01T09:00:00.000+09:00',
            to: '2023-01-02T09:00:00.000+09:00',
          },
        } as DiscountCoupon,
        '2023-01-02T09:00:01.000+09:00' as DateTime
      )
    ).toBe(false)
    expect(
      reservablePeriod(
        {
          reservablePeriod: {
            from: '2023-01-01T09:00:00.000+09:00',
            to: '2023-01-02T09:00:00.000+09:00',
          },
        } as DiscountCoupon,
        '2023-01-01T08:59:59.000+09:00' as DateTime
      )
    ).toBe(false)
  })
}

function visitablePeriod(coupon: DiscountCoupon, visit: DateText) {
  const from = coupon.visitablePeriod.from
  const to = coupon.visitablePeriod.to
  if (!from || !to) {
    return true
  }
  if (from && !to) {
    return from <= visit
  }
  if (!from && to) {
    return visit <= to
  }
  return from <= visit && visit <= to
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('visitablePeriod', async () => {
    expect(
      visitablePeriod(
        { visitablePeriod: { from: '2023-01-01', to: '2023-01-02' } } as DiscountCoupon,
        '2023-01-01' as DateText
      )
    ).toBe(true)
    expect(
      visitablePeriod(
        { visitablePeriod: { from: '2023-01-01', to: '2023-01-02' } } as DiscountCoupon,
        '2023-01-02' as DateText
      )
    ).toBe(true)
    expect(
      visitablePeriod(
        { visitablePeriod: { from: '2023-01-01', to: '2023-01-02' } } as DiscountCoupon,
        '2023-01-03' as DateText
      )
    ).toBe(false)
    expect(
      visitablePeriod(
        { visitablePeriod: { from: '2023-01-01', to: '2023-01-02' } } as DiscountCoupon,
        '2020-12-31' as DateText
      )
    ).toBe(false)
    expect(
      visitablePeriod(
        { visitablePeriod: { from: null, to: null } } as DiscountCoupon,
        '2023-01-01' as DateText
      )
    ).toBe(true)
    expect(
      visitablePeriod(
        { visitablePeriod: { from: '2023-01-01', to: null } } as DiscountCoupon,
        '2023-01-01' as DateText
      )
    ).toBe(true)
    expect(
      visitablePeriod(
        { visitablePeriod: { from: null, to: '2023-01-02' } } as DiscountCoupon,
        '2023-01-02' as DateText
      )
    ).toBe(true)
  })
}

export function usableTotalAmount(coupon: DiscountCoupon, totalAmount: number) {
  if (!coupon.minimumReservationAmount) {
    return true
  }
  return coupon.minimumReservationAmount <= totalAmount
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('usableTotalAmount', async () => {
    expect(usableTotalAmount({ minimumReservationAmount: 1000 } as DiscountCoupon, 1000)).toBe(true)
    expect(usableTotalAmount({ minimumReservationAmount: 1000 } as DiscountCoupon, 900)).toBe(false)
    expect(usableTotalAmount({ minimumReservationAmount: null } as DiscountCoupon, 900)).toBe(true)
  })
}

function addPriceToDuplicateName(coupons: ReserveInputDiscountCoupons) {
  return coupons.map((coupon) => {
    // 利用可能な同名のクーポンが複数ある場合は、クーポン名に割引額を付加する
    // 尚、クーポン名自体に割引額が含まれている場合を考慮し、一律に割引額を付加することはしていない
    const name =
      coupons.filter((c) => c.name === coupon.name).length > 1
        ? `${coupon.name} ${money(coupon.discountAmount)}円割引`
        : `${coupon.name}`
    return { ...coupon, name }
  })
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('addPriceToDuplicateName', async () => {
    expect(
      addPriceToDuplicateName([
        { name: '割引クーポン', discountAmount: 1000 } as DiscountCoupon,
        { name: '割引クーポン', discountAmount: 2000 } as DiscountCoupon,
        { name: '割引クーポン', discountAmount: 3000 } as DiscountCoupon,
        { name: '割引クーポン2', discountAmount: 2000 } as DiscountCoupon,
      ])
    ).toEqual([
      { name: '割引クーポン 1,000円割引', discountAmount: 1000 } as DiscountCoupon,
      { name: '割引クーポン 2,000円割引', discountAmount: 2000 } as DiscountCoupon,
      { name: '割引クーポン 3,000円割引', discountAmount: 3000 } as DiscountCoupon,
      { name: '割引クーポン2', discountAmount: 2000 } as DiscountCoupon,
    ])
  })
}

export const maxDiscountCoupon$ = atom((get) => {
  const coupons = get(discountCoupons$)
  return getMaxDiscountCoupon(coupons)
})

function getMaxDiscountCoupon(coupons: ReserveInputDiscountCoupons): SelectedDiscountCouponType {
  if (coupons.length === 0) {
    return { type: 'InputtedCouponCode' }
  }
  const coupon = coupons.reduce((max, coupon) => {
    return max.discountAmount > coupon.discountAmount ? max : coupon
  })
  return { type: 'AcquiredCoupon', code: coupon.code }
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('getMaxDiscountCoupon', async () => {
    expect(
      getMaxDiscountCoupon([
        { discountAmount: 1000, code: 'aaaa' } as DiscountCoupon,
        { discountAmount: 2000, code: 'bbbb' } as DiscountCoupon,
      ])
    ).toEqual({ type: 'AcquiredCoupon', code: 'bbbb' })
    expect(getMaxDiscountCoupon([])).toEqual({ type: 'InputtedCouponCode' })
  })
}

export const selectedCouponCode$ = atom<DiscountCouponCode | undefined>((get) => {
  const selectedDiscountCoupon = get(discountCouponType$)
  return selectedDiscountCoupon?.code
})
selectedCouponCode$.debugLabel = 'selectedCouponCode$'

export function useSelectedAcquiredCouponCode() {
  return useAtomValue(selectedCouponCode$)
}

const validateCouponQueryVariables$ = atom((get) => {
  const couponCode = get(selectedCouponCode$)
  const restaurantId = get(restaurantId$)
  const reserveInfo = get(reserveInfo$)
  const plan = get(plan$)
  const totalAmount = get(totalAmount$)
  // variables に totalAmount があるので、金額が変更されるたびに validateCouponQueryVariables$ が更新され、
  // クーポンが適用できなくなるとクーポンが外れるようになる
  return {
    input: {
      restaurantId,
      planId: plan?.planId ?? ('' as PlanId),
      code: couponCode ?? ('' as DiscountCouponCode),
      visitDate: reserveInfo?.visitDate ?? ('' as DateText),
      totalAmount,
      planPrice: plan?.price ?? 0,
      hash: reserveInfo?.discountCouponValidationHash ?? '',
    },
  }
})
validateCouponQueryVariables$.debugLabel = 'validateCouponQueryVariables$'

const validateCouponQueryFn$ = createQueryAtom<typeof ValidateDiscountCoupon>()
validateCouponQueryFn$.debugLabel = 'validateCouponQueryFn$'

const validateCouponQuery$ = atomWithQuery((get) => {
  const couponCode = get(selectedCouponCode$)
  const reserveInfo = get(reserveInfo$)
  const plan = get(plan$)
  return createQueryOptions(
    ValidateDiscountCoupon,
    get(validateCouponQueryVariables$),
    get(validateCouponQueryFn$).fn,
    {
      enabled: couponCode !== undefined && reserveInfo !== undefined && plan !== undefined,
    }
  )
})
validateCouponQuery$.debugLabel = 'validateCouponQuery$'

// ../specification.md 「atomEffect について」参照
export const listenValidateCouponQuery = atomEffect((get, set) => {
  if (get(isInitialDisplay$)) {
    return
  }
  const query = get(validateCouponQuery$)
  if (query.status === 'success') {
    set(transition$, onLoadEvent(query.data))
  }
})

function onLoadEvent(query: ResultOf<typeof ValidateDiscountCoupon>): ReservationInputEvent {
  return query.validateDiscountCoupon.__typename == 'DiscountCoupon'
    ? {
        type: 'updated',
        appliedDiscountCoupon: query.validateDiscountCoupon,
      }
    : // クーポン適用できない場合
      {
        type: 'updated',
        discountCouponType: {
          type: 'InputtedCouponCode',
          errorMessage: query.validateDiscountCoupon.message,
        },
        appliedDiscountCoupon: undefined,
      }
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('onLoadEvent', async () => {
    expect(
      onLoadEvent({
        validateDiscountCoupon: {
          __typename: 'DiscountCoupon',
          code: '1234567890',
          name: '割引クーポン',
          discountAmount: 1000,
        },
      } as ResultOf<typeof ValidateDiscountCoupon>)
    ).toEqual({
      type: 'updated',
      appliedDiscountCoupon: {
        __typename: 'DiscountCoupon',
        code: '1234567890',
        name: '割引クーポン',
        discountAmount: 1000,
      },
    })
    expect(
      onLoadEvent({
        validateDiscountCoupon: {
          __typename: 'DiscountCouponError',
          message: 'クーポンコードが不正です',
        },
      } as unknown as ResultOf<typeof ValidateDiscountCoupon>)
    ).toEqual({
      type: 'updated',
      discountCouponType: {
        type: 'InputtedCouponCode',
        errorMessage: 'クーポンコードが不正です',
      },
      appliedDiscountCoupon: undefined,
    })
  })
}

export const popupCouponDisplayed$ = scope.atom(false)

export type DisplayPointAndCouponType = 'pointUnavailable' | 'seatOnly' | 'popupCouponDisplayed'

const displayPointAndCouponType$ = atom<DisplayPointAndCouponType | undefined>((get) => {
  const restaurant = get(restaurant$)
  const plan = get(plan$)
  const isGuest = get(isGuest$)
  const popupCouponDisplayed = get(popupCouponDisplayed$)
  return displayPointAndCouponType({ restaurant, plan, isGuest, popupCouponDisplayed })
})
displayPointAndCouponType$.debugLabel = 'displayPointAndCouponType$'

export function useDisplayPointAndCouponType() {
  return useAtomValue(displayPointAndCouponType$)
}

function displayPointAndCouponType(data: {
  restaurant?: ReserveInputRestaurant
  plan?: ReserveInputPlan
  isGuest: boolean
  popupCouponDisplayed?: boolean
}): DisplayPointAndCouponType | undefined {
  if (data.isGuest) {
    if (data.restaurant?.pointAvailable && !data.plan?.isSeatOnly && data.popupCouponDisplayed) {
      return 'popupCouponDisplayed'
    }
    return undefined
  }
  if (!data.restaurant?.pointAvailable) {
    return 'pointUnavailable'
  }
  if (data.plan?.isSeatOnly) {
    return 'seatOnly'
  }
  return undefined
}
if (import.meta.vitest) {
  const { test, expect } = import.meta.vitest
  test.each`
    isGuest  | pointAvailable | isSeatOnly | popupCouponDisplayed | expected
    ${false} | ${false}       | ${false}   | ${false}             | ${'pointUnavailable'}
    ${false} | ${false}       | ${false}   | ${true}              | ${'pointUnavailable'}
    ${false} | ${false}       | ${true}    | ${false}             | ${'pointUnavailable'}
    ${false} | ${false}       | ${true}    | ${true}              | ${'pointUnavailable'}
    ${false} | ${true}        | ${false}   | ${false}             | ${undefined}
    ${false} | ${true}        | ${false}   | ${true}              | ${undefined}
    ${false} | ${true}        | ${true}    | ${false}             | ${'seatOnly'}
    ${false} | ${true}        | ${true}    | ${true}              | ${'seatOnly'}
    ${true}  | ${false}       | ${false}   | ${false}             | ${undefined}
    ${true}  | ${false}       | ${false}   | ${true}              | ${undefined}
    ${true}  | ${false}       | ${true}    | ${false}             | ${undefined}
    ${true}  | ${false}       | ${true}    | ${true}              | ${undefined}
    ${true}  | ${true}        | ${false}   | ${false}             | ${undefined}
    ${true}  | ${true}        | ${false}   | ${true}              | ${'popupCouponDisplayed'}
    ${true}  | ${true}        | ${true}    | ${false}             | ${undefined}
    ${true}  | ${true}        | ${true}    | ${true}              | ${undefined}
  `(
    'displayPointAndCouponType({ restaurant: { pointAvailable: $pointAvailable }, plan: { isSeatOnly: $isSeatOnly }, isGuest: $isGuest, popupCouponDisplayed: $popupCouponDisplayed }) === $expected',
    async ({ pointAvailable, isSeatOnly, isGuest, popupCouponDisplayed, expected }) => {
      expect(
        displayPointAndCouponType({
          restaurant: { pointAvailable } as ReserveInputRestaurant,
          plan: { isSeatOnly } as ReserveInputPlan,
          isGuest,
          popupCouponDisplayed,
        })
      ).toBe(expected)
    }
  )
}

export function useApplyAcquiredCoupon() {
  const send = useTransition()
  return useCallback(
    (code: DiscountCouponCode) => {
      send({
        type: 'updated',
        discountCouponType: {
          type: 'AcquiredCoupon',
          code: code,
        },
        // undefined にすると event が発火せず元の値が保持されるので null にする
        appliedDiscountCoupon: null,
      })
    },
    [send]
  )
}

export function useApplyInputCouponCode() {
  const send = useTransition()
  return useCallback(() => {
    send({
      type: 'updated',
      discountCouponType: {
        type: 'InputtedCouponCode',
        code: undefined,
      },
      // undefined にすると event が発火せず元の値が保持されるので null にする
      appliedDiscountCoupon: null,
    })
  }, [send])
}

export function useSubmitInputCoupon() {
  const send = useTransition()
  return useCallback(
    (code?: DiscountCouponCode) => {
      send({
        type: 'updated',
        discountCouponType: {
          type: 'InputtedCouponCode',
          code: code,
        },
      })
    },
    [send]
  )
}

export function useCheckIsDiscountCouponEnabled() {
  const send = useTransition()
  return useCallback(
    (v: boolean) => {
      send({
        type: 'updated',
        isDiscountCouponEnabled: v,
      })
    },
    [send]
  )
}
